import { useState } from "react"
import { Link } from "wouter"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { useToast } from "@/hooks/use-toast"
import { useQuery, useMutation } from "@tanstack/react-query"
import { apiRequest, queryClient } from "@/lib/queryClient"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import type { ContentItem, InsertContentItem, ContentUpload } from "@shared/schema"
import { insertContentItemSchema } from "@shared/schema"
import { 
  Plus, 
  Edit, 
  Trash2, 
  FileText, 
  ExternalLink,
  Filter,
  Calendar,
  BarChart3,
  Images,
  ArrowRight
} from "lucide-react"
import { format } from "date-fns"
import { de } from "date-fns/locale"

const contentCategories = [
  "Blog Artikel",
  "Social Media Post", 
  "Video Content",
  "Infografik",
  "Podcast",
  "Webinar",
  "E-Book",
  "Case Study",
  "Newsletter",
  "Andere"
]

const platforms = [
  "LinkedIn",
  "Facebook", 
  "Instagram",
  "Twitter/X",
  "YouTube",
  "Blog/Website",
  "Email",
  "Andere"
]

export function Content() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<ContentItem | null>(null)
  const [filterCategory, setFilterCategory] = useState<string>("all")
  const [searchTerm, setSearchTerm] = useState("")

  const { toast } = useToast()

  // Form for adding/editing content
  const form = useForm<InsertContentItem>({
    resolver: zodResolver(insertContentItemSchema),
    defaultValues: {
      title: "",
      description: "",
      category: "",
      source: "manual",
      platform: "",
      url: ""
    }
  })

  // Fetch content items
  const { data: contentItems = [], isLoading } = useQuery({
    queryKey: ['/api/content'],
    select: (data) => data as ContentItem[]
  })

  // Fetch approved uploads count for photo library link
  const { data: approvedUploads = [] } = useQuery<ContentUpload[]>({
    queryKey: ['/api/uploads/my-approved']
  })

  // Add content mutation
  const addContentMutation = useMutation({
    mutationFn: (data: InsertContentItem) => apiRequest('POST', '/api/content', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/content'] })
      setIsAddDialogOpen(false)
      form.reset()
      toast({
        title: "Content hinzugefügt",
        description: "Der Content wurde erfolgreich erstellt."
      })
    },
    onError: () => {
      toast({
        title: "Fehler beim Hinzufügen",
        description: "Content konnte nicht erstellt werden.",
        variant: "destructive"
      })
    }
  })

  // Update content mutation
  const updateContentMutation = useMutation({
    mutationFn: ({ id, data }: { id: string, data: InsertContentItem }) => 
      apiRequest('PATCH', `/api/content/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/content'] })
      setEditingItem(null)
      form.reset()
      toast({
        title: "Content aktualisiert",
        description: "Die Änderungen wurden gespeichert."
      })
    },
    onError: () => {
      toast({
        title: "Fehler beim Aktualisieren",
        description: "Änderungen konnten nicht gespeichert werden.",
        variant: "destructive"
      })
    }
  })

  // Delete content mutation
  const deleteContentMutation = useMutation({
    mutationFn: (id: string) => apiRequest('DELETE', `/api/content/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/content'] })
      toast({
        title: "Content gelöscht",
        description: "Der Content wurde erfolgreich entfernt."
      })
    },
    onError: () => {
      toast({
        title: "Fehler beim Löschen",
        description: "Content konnte nicht gelöscht werden.",
        variant: "destructive"
      })
    }
  })

  const handleEdit = (item: ContentItem) => {
    setEditingItem(item)
    form.reset({
      title: item.title,
      description: item.description || "",
      category: item.category,
      source: item.source || "manual",
      platform: item.platform || "",
      url: item.url || ""
    })
  }

  const handleSubmit = (data: InsertContentItem) => {
    if (editingItem) {
      updateContentMutation.mutate({ id: editingItem.id, data })
    } else {
      addContentMutation.mutate(data)
    }
  }

  const handleDelete = (id: string) => {
    if (confirm("Sind Sie sicher, dass Sie diesen Content löschen möchten?")) {
      deleteContentMutation.mutate(id)
    }
  }

  const handleDialogClose = () => {
    setIsAddDialogOpen(false)
    setEditingItem(null)
    form.reset()
  }

  // Filter content items
  const filteredItems = contentItems
    .filter(item => filterCategory === "all" || item.category === filterCategory)
    .filter(item => 
      searchTerm === "" || 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
    )

  return (
    <div className="container max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight font-playfair">Content-Verwaltung</h1>
        <p className="text-muted-foreground">
          Verwalten Sie Ihre Content-Bibliothek für AI-gestützte Marketing-Kampagnen
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gesamt Content</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{contentItems.length}</div>
            <p className="text-xs text-muted-foreground">Content-Elemente in der Bibliothek</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Oft verwendet</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {contentItems.reduce((sum, item) => sum + (item.usageCount || 0), 0)}
            </div>
            <p className="text-xs text-muted-foreground">Gesamte Nutzungen</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Kategorien</CardTitle>
            <Filter className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(contentItems.map(item => item.category)).size}
            </div>
            <p className="text-xs text-muted-foreground">Verschiedene Kategorien</p>
          </CardContent>
        </Card>

        {/* Foto-Bibliothek Link Card */}
        <Card className="hover-elevate cursor-pointer transition-all duration-300 border-primary/20 hover:border-primary/40">
          <Link href="/foto-bibliothek">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-primary">Foto-Bibliothek</CardTitle>
              <Images className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-primary">{approvedUploads.length}</div>
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-muted-foreground">Freigegebene Photos</p>
                <div className="flex items-center text-xs text-primary font-medium">
                  <span>Ansehen</span>
                  <ArrowRight className="h-3 w-3 ml-1 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </CardContent>
          </Link>
        </Card>
      </div>

      {/* Filters and Add Button */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Input
            placeholder="Content durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full sm:w-64"
            data-testid="input-search-content"
          />
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-full sm:w-48" data-testid="select-filter-category">
              <SelectValue placeholder="Kategorie filtern" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Kategorien</SelectItem>
              {contentCategories.map(category => (
                <SelectItem key={category} value={category}>{category}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Dialog open={isAddDialogOpen || !!editingItem} onOpenChange={(open) => {
          if (!open) handleDialogClose()
        }}>
          <DialogTrigger asChild>
            <Button className="w-full sm:w-auto" onClick={() => setIsAddDialogOpen(true)} data-testid="button-add-content">
              <Plus className="mr-2 h-4 w-4" />
              Content hinzufügen
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>
                {editingItem ? "Content bearbeiten" : "Neuen Content hinzufügen"}
              </DialogTitle>
              <DialogDescription>
                Fügen Sie Content zu Ihrer Bibliothek hinzu für AI-gestützte Marketing-Kampagnen.
              </DialogDescription>
            </DialogHeader>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Titel *</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="Content-Titel eingeben..." 
                          data-testid="input-content-title"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Beschreibung</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Kurze Beschreibung des Contents..."
                          rows={3}
                          data-testid="textarea-content-description"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Kategorie *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value || ""}>
                          <FormControl>
                            <SelectTrigger data-testid="select-content-category">
                              <SelectValue placeholder="Kategorie wählen" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {contentCategories.map(category => (
                              <SelectItem key={category} value={category}>
                                {category}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="platform"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Plattform</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value || ""}>
                          <FormControl>
                            <SelectTrigger data-testid="select-content-platform">
                              <SelectValue placeholder="Plattform wählen" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {platforms.map(platform => (
                              <SelectItem key={platform} value={platform}>
                                {platform}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="source"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Quelle</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="z.B. Unternehmensname"
                            data-testid="input-content-source"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="url"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>URL</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://..."
                            data-testid="input-content-url"
                            {...field}
                            value={field.value || ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <DialogFooter>
                  <Button 
                    type="button"
                    variant="outline" 
                    onClick={handleDialogClose}
                    data-testid="button-cancel-content"
                  >
                    Abbrechen
                  </Button>
                  <Button 
                    type="submit"
                    disabled={addContentMutation.isPending || updateContentMutation.isPending}
                    data-testid="button-save-content"
                  >
                    {addContentMutation.isPending || updateContentMutation.isPending ? "Speichert..." : 
                     editingItem ? "Aktualisieren" : "Hinzufügen"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Content Table */}
      <Card>
        <CardHeader>
          <CardTitle>Content-Bibliothek</CardTitle>
          <CardDescription>
            {filteredItems.length} von {contentItems.length} Content-Elementen
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm || filterCategory !== "all" ? 
                "Keine Content-Elemente entsprechen den aktuellen Filtern." :
                "Noch keine Content-Elemente vorhanden. Fügen Sie Ihren ersten Content hinzu!"
              }
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titel</TableHead>
                  <TableHead>Kategorie</TableHead>
                  <TableHead>Plattform</TableHead>
                  <TableHead>Verwendungen</TableHead>
                  <TableHead>Erstellt</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium">{item.title}</div>
                        {item.description && (
                          <div className="text-sm text-muted-foreground truncate max-w-xs">
                            {item.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{item.category}</Badge>
                    </TableCell>
                    <TableCell>
                      {item.platform ? (
                        <Badge variant="outline">{item.platform}</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <BarChart3 className="h-3 w-3 text-muted-foreground" />
                        {item.usageCount || 0}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {item.createdAt ? format(new Date(item.createdAt), "dd.MM.yyyy", { locale: de }) : "-"}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {item.url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(item.url!, '_blank')}
                            data-testid={`button-view-${item.id}`}
                          >
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(item)}
                          data-testid={`button-edit-${item.id}`}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(item.id)}
                          data-testid={`button-delete-${item.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}